import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Button, Spin, Typography, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { FileTextOutlined } from '@ant-design/icons';
import {
  getSelectedFolder,
  getSelectedTag,
  getSelectedTopic,
  getSelectedKB,
  getSearchKeyword,
  getQaHistory,
  setQaHistory
} from '../../hooks/useDashboardGlobalVar';
import { renderMarkdownSafely } from '../../pages/DashboardUtils';
import { useNoteList } from '../../contexts/NoteListContext';

const { Text } = Typography;
const { TextArea } = Input;

// Sparkle icon component matching Figma design
const SparklesIcon = ({ style, className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    className={className}
  >
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
    <path d="M19 13l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" />
    <path d="M5 17l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
  </svg>
);

// Send icon component
const SendIcon = ({ style }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// 历史容量配置：从 5 对增加到 10 对（共 20 条消息）
const MAX_HISTORY_PAIRS = 10;
const MAX_HISTORY_MESSAGES = MAX_HISTORY_PAIRS * 2;

// 清理回答内容中的内容片段标记
const cleanAnswerContent = (content) => {
  if (!content || typeof content !== 'string') {
    return content;
  }
  let cleaned = content;

  // 移除【内容片段 X、Y】或【内容片段X、Y】等格式（中文方括号）
  cleaned = cleaned.replace(/【内容片段[^】]*】/g, '');

  // 移除 [内容片段] 格式（英文方括号）
  cleaned = cleaned.replace(/\[内容片段[^\]]*\]/g, '');

  // 移除 (内容片段) 格式（圆括号）
  cleaned = cleaned.replace(/\(内容片段[^\)]*\)/g, '');

  // 移除参考信息后面的【内容片段X、Y】标记
  // 匹配 "> 参考:标题(复制)\n【内容片段X、Y】" 这样的格式，只保留 "> 参考:标题(复制)"
  cleaned = cleaned.replace(/(>[\s]*参考[^：:]*[：:][^\n]*?)(\(复制\))?[\s]*\n[\s]*【内容片段[^】]*】/g, '$1$2');
  cleaned = cleaned.replace(/(>[\s]*参考[^：:]*[：:][^\n]*?)(\(复制\))?[\s]*\n[\s]*\[内容片段[^\]]*\]/g, '$1$2');
  cleaned = cleaned.replace(/(>[\s]*参考[^：:]*[：:][^\n]*?)(\(复制\))?[\s]*\n[\s]*\(内容片段[^\)]*\)/g, '$1$2');

  // 清理多余的空行（保留最多两个连续空行）
  cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');

  // 清理行尾空白
  cleaned = cleaned.replace(/[ \t]+$/gm, '');

  return cleaned.trim();
};

const NoteListAsk = ({
  onClose,
  onBackToSummary,
  qaContentRef,
  width = '30%', // Default width for left panel, can be '100%' for right panel
  embedded = false, // Whether this component is embedded in RightContentPane
  initialQuestion = null, // Initial question to auto-trigger QA search
  selectedNote = null // Selected note passed from parent
}) => {
  const { t } = useTranslation(['dashboard', 'common']);

  // 获取当前笔记列表数据（用于获取搜索返回的所有笔记ID）
  const { displayNotes, searchResults } = useNoteList();

  // Context selection state - 'all' or 'current'
  const [contextMode, setContextMode] = useState('all');

  // === "Ask all notes" flow state ===
  const [allIsQASearchLoading, setAllIsQASearchLoading] = useState(false);
  const [allQaHistory, setAllQaHistoryLocal] = useState(() => {
    const globalHistory = getQaHistory();
    return Array.isArray(globalHistory) ? globalHistory : [];
  });
  const allQaHistoryRef = useRef([]);
  const allQaSearchRef = useRef(null);
  const [allQuestionInput, setAllQuestionInput] = useState('');

  // === "Ask about current note" flow state ===
  const [currentIsQASearchLoading, setCurrentIsQASearchLoading] = useState(false);
  const [currentQaHistory, setCurrentQaHistoryLocal] = useState([]);
  const currentQaHistoryRef = useRef([]);
  const currentQaSearchRef = useRef(null);
  const [currentQuestionInput, setCurrentQuestionInput] = useState('');

  // Clear both flows' history when selectedNote changes
  useEffect(() => {
    setCurrentQaHistoryLocal([]);
    currentQaHistoryRef.current = [];
    setCurrentQuestionInput('');

    setAllQaHistoryLocal([]);
    allQaHistoryRef.current = [];
    setAllQuestionInput('');
    setQaHistory([], 'NoteListAsk');
  }, [selectedNote?.id]);

  // === Derived state based on contextMode (read-only aliases for rendering/handlers) ===
  const isQASearchLoading = contextMode === 'all' ? allIsQASearchLoading : currentIsQASearchLoading;
  const qaHistory = contextMode === 'all' ? allQaHistory : currentQaHistory;
  const qaSearchRef = contextMode === 'all' ? allQaSearchRef : currentQaSearchRef;
  const questionInput = contextMode === 'all' ? allQuestionInput : currentQuestionInput;
  const setQuestionInput = contextMode === 'all' ? setAllQuestionInput : setCurrentQuestionInput;

  // 同步 "all" qaHistory 到 ref 和全局状态
  useEffect(() => {
    allQaHistoryRef.current = allQaHistory;
    setQaHistory(allQaHistory, 'NoteListAsk');
  }, [allQaHistory]);

  // 同步 "current" qaHistory 到 ref
  useEffect(() => {
    currentQaHistoryRef.current = currentQaHistory;
  }, [currentQaHistory]);

  // 组件挂载时，从全局状态恢复 "all" 历史记录
  useEffect(() => {
    const globalHistory = getQaHistory();
    if (Array.isArray(globalHistory) && globalHistory.length > 0) {
      console.log('[NoteListAsk] 从全局状态恢复 QA 历史记录:', {
        historyLength: globalHistory.length
      });
      setAllQaHistoryLocal(globalHistory);
    }
  }, []); // 只在组件挂载时执行一次
  
  // 跟踪已处理的初始问题，避免重复触发
  const processedInitialQuestionRef = useRef(null);

  // 跟踪上一次的流式状态，用于检测流式完成
  const prevStreamingStateRef = useRef(new Map());
  // 跟踪最后一条用户消息ID，用于滚动定位
  const lastUserMessageIdRef = useRef(null);

  // 滚动到用户消息位置的辅助函数，确保问题显示在显示区域最顶部，同时AI thinking也可见
  const scrollToUserMessage = useCallback((messageId, immediate = false) => {
    if (!qaContentRef?.current || !messageId) return;

    const performScroll = () => {
      const container = qaContentRef.current;
      if (!container) return;
      
      const userMessageElement = container.querySelector(`[data-question-id="${messageId}"]`);
      
      if (userMessageElement) {
        // 找到问题所在的问答对容器（包含问题和回答的父容器）
        // 问答对容器有 data-qa-pair-id 属性，值等于 question.id
        const qaPairContainer = container.querySelector(`[data-qa-pair-id="${messageId}"]`);
        
        // 如果找到问答对容器，使用它；否则使用问题元素本身
        const targetElement = qaPairContainer || userMessageElement;
        
        // 计算目标元素相对于容器的精确位置
        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const currentScrollTop = container.scrollTop;
        
        // 计算目标元素相对于容器内容区域的顶部位置
        const relativeTop = targetRect.top - containerRect.top + currentScrollTop;
        
        // 获取容器的 padding-top（如果有）
        const containerPaddingTop = parseInt(window.getComputedStyle(container).paddingTop, 10) || 0;
        
        // 滚动到目标元素顶部，确保问题显示在容器最顶端（减去 padding）
        // 这样问题在顶部，AI thinking 在下面可见
        container.scrollTop = relativeTop - containerPaddingTop;
        
        console.log('[NoteListAsk] 滚动到问题顶部（包含AI thinking）:', {
          messageId,
          relativeTop,
          containerPaddingTop,
          scrollTop: container.scrollTop,
          hasQAPairContainer: !!qaPairContainer,
          immediate
        });
      } else {
        // 如果找不到元素，延迟重试
        if (!immediate) {
          setTimeout(() => performScroll(), 100);
        }
      }
    };

    // 如果 immediate 为 true，立即尝试滚动；否则延迟一下
    if (immediate) {
      requestAnimationFrame(() => {
        requestAnimationFrame(performScroll);
      });
    } else {
      setTimeout(performScroll, 150);
    }
  }, [qaContentRef]);

  // 当流式结果完成时，滚动到最后的问题顶部
  useEffect(() => {
    // 如果 qaHistory 为空，清理流式状态跟踪
    if (qaHistory.length === 0) {
      prevStreamingStateRef.current.clear();
      lastUserMessageIdRef.current = null;
      return;
    }

    if (!qaContentRef?.current) return;

    // 检测是否有新的用户问题（用于记录，但不立即滚动）
    // 遍历 qaHistory 找到所有用户消息，检查是否有新的用户消息ID
    const userMessages = qaHistory.filter(msg => msg.type === 'user');
    if (userMessages.length > 0) {
      const lastUserMessage = userMessages[userMessages.length - 1];
      const lastUserMessageId = lastUserMessage.id;
      
      // 如果检测到新的用户消息ID（与上次记录的不同），更新记录
      if (lastUserMessageIdRef.current !== lastUserMessageId) {
        console.log('[NoteListAsk] 检测到新的用户问题:', {
          oldId: lastUserMessageIdRef.current,
          newId: lastUserMessageId
        });
        lastUserMessageIdRef.current = lastUserMessageId;
        
        // 新问题时，滚动到底部（显示新问题+AI thinking）
        // 不再滚动到问题顶部，而是滚动到底部，让用户看到最新的内容
        if (qaContentRef.current) {
          setTimeout(() => {
            if (qaContentRef.current) {
              qaContentRef.current.scrollTop = qaContentRef.current.scrollHeight;
            }
          }, 100);
        }
      }
    }

    // 检测流式状态，在流式显示过程中自动滚动到底部
    let hasStreamingAI = false;
    qaHistory.forEach((msg) => {
      if (msg.type === 'ai' && msg.isStreaming) {
        hasStreamingAI = true;
        // 更新流式状态
        prevStreamingStateRef.current.set(msg.id, true);
      } else if (msg.type === 'ai') {
        // 更新流式状态
        const prevIsStreaming = prevStreamingStateRef.current.get(msg.id);
        const currentIsStreaming = msg.isStreaming || false;
        prevStreamingStateRef.current.set(msg.id, currentIsStreaming);
      }
    });

    // 如果有正在流式显示的AI回答，自动滚动到底部（跟随流式内容）
    if (hasStreamingAI && qaContentRef?.current) {
      // 使用 requestAnimationFrame 确保在 DOM 更新后立即滚动
      requestAnimationFrame(() => {
        if (qaContentRef.current) {
          qaContentRef.current.scrollTop = qaContentRef.current.scrollHeight;
        }
      });
    }

    // 流式完成后，确保滚动到底部
    const hasAnyAI = qaHistory.some(msg => msg.type === 'ai');
    if (hasAnyAI && !hasStreamingAI && qaContentRef?.current) {
      // 所有流式都完成了，确保滚动到底部
      setTimeout(() => {
        if (qaContentRef.current) {
          qaContentRef.current.scrollTop = qaContentRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [qaHistory, isQASearchLoading, qaContentRef]);

  // === Helper: shared QA request logic ===
  const executeQaRequest = useCallback(async (question, qaHistoryString, noteIdlist, requestFilters) => {
    const { latestFolder, latestTag, latestTopic, latestKB } = requestFilters;

    const requestBody = {
      question: question.trim(),
      qaHistory: qaHistoryString,
      filterType: latestFolder === 'private' ? 'private' :
        latestFolder === 'collaborative' ? 'collaborative' :
          latestFolder === 'pinned' ? 'pinned' :
            latestFolder === 'recent' ? 'recent' : 'all',
      topicId: latestTopic ? latestTopic.topic_id : '',
      kbId: latestKB ? latestKB.kb_id : '',
      selectedTags: latestTag ? latestTag.name : '',
      searchKeyword: '',
      noteIds: noteIdlist
    };

    console.log('[NoteListAsk] 执行 QA 搜索:', { requestBody });

    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    const authStorage = localStorage.getItem('auth-storage');
    const token = authStorage ? JSON.parse(authStorage).state?.token : null;

    const response = await fetch(`${baseUrl}/notes/qa/multi-notes-qa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 402) {
        throw new Error('INSUFFICIENT_CREDITS');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    let answer = result.answer || '';
    const fetchedReferences = result.references || [];
    const fetchedConfidence = result.confidence || 0.8;

    // 解析 JSON 响应，提取 answer 字段
    let finalAnswer = answer;
    try {
      let responseText = answer.trim();
      if (responseText.includes('```json')) {
        const jsonStart = responseText.indexOf('```json') + 7;
        const jsonEnd = responseText.indexOf('```', jsonStart);
        if (jsonEnd !== -1) {
          responseText = responseText.substring(jsonStart, jsonEnd).trim();
        }
      } else if (responseText.includes('```')) {
        const jsonStart = responseText.indexOf('```') + 3;
        const jsonEnd = responseText.indexOf('```', jsonStart);
        if (jsonEnd !== -1) {
          responseText = responseText.substring(jsonStart, jsonEnd).trim();
        }
      }
      if (responseText.startsWith('{')) {
        let parsedResponse = null;
        try {
          parsedResponse = JSON.parse(responseText);
        } catch (e) {
          if (responseText.includes("'")) {
            try {
              let normalizedText = responseText;
              normalizedText = normalizedText.replace(/'(\w+)':/g, '"$1":');
              normalizedText = normalizedText.replace(/: '((?:[^'\\]|\\.)*)'/g, (match, content) => {
                const escaped = content.replace(/"/g, '\\"');
                return `: "${escaped}"`;
              });
              normalizedText = normalizedText.replace(/\[((?:'[^']*'(?:,\s*'[^']*')*)?)\]/g, (match, content) => {
                if (content) {
                  const replaced = content.replace(/'([^']*)'/g, '"$1"');
                  return `[${replaced}]`;
                }
                return '[]';
              });
              parsedResponse = JSON.parse(normalizedText);
              console.log('[NoteListAsk] 成功将 Python 字典格式转换为 JSON');
            } catch (convertError) {
              console.warn('[NoteListAsk] Python 字典格式转换失败:', convertError);
              throw convertError;
            }
          } else {
            throw e;
          }
        }
        if (parsedResponse && parsedResponse.answer) {
          finalAnswer = parsedResponse.answer;
          console.log('[NoteListAsk] 成功解析 JSON，提取 answer 字段，长度:', finalAnswer.length);
        } else if (parsedResponse) {
          console.warn('[NoteListAsk] JSON 解析成功但缺少 answer 字段，使用原始内容');
        }
      }
    } catch (parseError) {
      console.warn('[NoteListAsk] JSON 解析失败，使用原始内容:', parseError);
      finalAnswer = answer;
    }

    return { finalAnswer, fetchedReferences, fetchedConfidence };
  }, []);

  // === "Ask all notes" search function ===
  const qaSearchAll = useCallback(async (question, searchMode = 'hybrid') => {
    if (!question || !question.trim()) {

      return null;
    }

    const currentFolder = getSelectedFolder();
    const currentTag = getSelectedTag();
    const currentTopic = getSelectedTopic();
    const currentKB = getSelectedKB();
    const currentSearchKeyword = getSearchKeyword();

    console.log('[NoteListAsk] qaSearchAll - 筛选条件:', {
      folder: currentFolder,
      tag: currentTag?.name || null,
      topic: currentTopic?.name || null,
      kb: currentKB?.name || null,
      searchKeyword: currentSearchKeyword || null
    });

    setAllIsQASearchLoading(true);

    const userMessageId = Date.now();
    const aiMessageId = userMessageId + 1;

    const currentHistory = allQaHistoryRef.current || [];
    const newHistory = [...currentHistory];
    newHistory.push({
      id: userMessageId, type: 'user', content: question.trim(),
      timestamp: new Date().toLocaleTimeString()
    });
    newHistory.push({
      id: aiMessageId, type: 'ai', content: '', references: [], confidence: 0,
      timestamp: new Date().toLocaleTimeString(), isStreaming: true
    });
    const updatedHistory = newHistory.slice(-MAX_HISTORY_MESSAGES);
    allQaHistoryRef.current = updatedHistory;
    setAllQaHistoryLocal(updatedHistory);

    try {
      const recentHistory = currentHistory.slice(-MAX_HISTORY_MESSAGES);
      const qaHistoryArray = recentHistory.map(msg => {
        if (msg.type === 'user') return { role: 'user', content: msg.content };
        if (msg.type === 'ai') return { role: 'assistant', content: msg.content };
        return null;
      }).filter(Boolean);
      const qaHistoryString = qaHistoryArray.length > 0 ? JSON.stringify(qaHistoryArray) : '';

      const latestFolder = getSelectedFolder();
      const latestTag = getSelectedTag();
      const latestTopic = getSelectedTopic();
      const latestKB = getSelectedKB();
      const latestSearchKeyword = getSearchKeyword();

      // "Ask all notes" noteIds logic
      let noteIdlist = '';
      if (latestSearchKeyword && latestSearchKeyword.trim().length > 0) {
        const notesToUse = searchResults && searchResults.length > 0 ? searchResults : displayNotes;
        const noteIds = notesToUse.filter(note => note && note.id).map(note => note.id.toString());
        noteIdlist = noteIds.length > 0 ? noteIds.join(',') : '';

        console.log('[NoteListAsk] qaSearchAll - 搜索关键词模式:', {
          searchKeyword: latestSearchKeyword, noteIdsCount: noteIds.length, noteIdlist
        });

        if (!noteIdlist || noteIdlist.trim().length === 0) {
          const errorMsg = 'Sorry, no reference materials available, unable to answer.';
          setAllQaHistoryLocal(prev => prev.map(msg =>
            msg.id === aiMessageId ? { ...msg, content: errorMsg, isStreaming: false } : msg
          ));
          allQaHistoryRef.current = allQaHistoryRef.current.map(msg =>
            msg.id === aiMessageId ? { ...msg, content: errorMsg, isStreaming: false } : msg
          );
          setAllIsQASearchLoading(false);
          return null;
        }
      }

      const { finalAnswer, fetchedReferences, fetchedConfidence } = await executeQaRequest(
        question, qaHistoryString, noteIdlist,
        { latestFolder, latestTag, latestTopic, latestKB }
      );

      setAllQaHistoryLocal(prev => prev.map(msg =>
        msg.id === aiMessageId ? { ...msg, content: finalAnswer, references: fetchedReferences, confidence: fetchedConfidence, isStreaming: false } : msg
      ));
      allQaHistoryRef.current = allQaHistoryRef.current.map(msg =>
        msg.id === aiMessageId ? { ...msg, content: finalAnswer, references: fetchedReferences, confidence: fetchedConfidence, isStreaming: false } : msg
      );

      const qaResult = { answer: finalAnswer, references: fetchedReferences, confidence: fetchedConfidence, timestamp: new Date().toLocaleTimeString() };

      return qaResult;

    } catch (error) {
      console.error('[NoteListAsk] qaSearchAll 失败:', error);
      const errorMessage = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('dashboard:qa_service_unavailable', { defaultValue: 'Sorry, the Q&A service is temporarily unavailable. Please try again later.' });
      setAllQaHistoryLocal(prev => prev.map(msg =>
        msg.id === aiMessageId ? { ...msg, content: errorMessage, isStreaming: false } : msg
      ));

      return null;
    } finally {
      setAllIsQASearchLoading(false);
    }
  }, [searchResults, displayNotes, executeQaRequest, t]);

  // === "Ask about current note" search function ===
  const qaSearchCurrent = useCallback(async (question, searchMode = 'hybrid') => {
    if (!question || !question.trim()) {

      return null;
    }

    if (!selectedNote || !selectedNote.id) {
      console.warn('[NoteListAsk] qaSearchCurrent - no selectedNote available');
      return null;
    }

    const noteIdlist = selectedNote.id.toString();
    console.log('[NoteListAsk] qaSearchCurrent - 使用当前笔记ID:', { noteId: selectedNote.id });

    setCurrentIsQASearchLoading(true);

    const userMessageId = Date.now();
    const aiMessageId = userMessageId + 1;

    const currentHistory = currentQaHistoryRef.current || [];
    const newHistory = [...currentHistory];
    newHistory.push({
      id: userMessageId, type: 'user', content: question.trim(),
      timestamp: new Date().toLocaleTimeString()
    });
    newHistory.push({
      id: aiMessageId, type: 'ai', content: '', references: [], confidence: 0,
      timestamp: new Date().toLocaleTimeString(), isStreaming: true
    });
    const updatedHistory = newHistory.slice(-MAX_HISTORY_MESSAGES);
    currentQaHistoryRef.current = updatedHistory;
    setCurrentQaHistoryLocal(updatedHistory);

    try {

      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;

      // Build qaHistory pairs from completed messages (exclude current question + placeholder)
      const completedMessages = currentHistory.filter(msg => !msg.isStreaming && msg.content);
      const qaHistoryPairs = [];
      for (let i = 0; i < completedMessages.length - 1; i += 2) {
        const userMsg = completedMessages[i];
        const aiMsg = completedMessages[i + 1];
        if (userMsg?.type === 'user' && aiMsg?.type === 'ai') {
          qaHistoryPairs.push({ user: userMsg.content, assistant: aiMsg.content });
        }
      }

      const requestBody = {
        noteId: selectedNote.id,
        question: question.trim(),
        qaHistory: qaHistoryPairs
      };

      console.log('[NoteListAsk] qaCurrentNote - 使用 /notes/qa/chat 端点:', {
        noteId: selectedNote.id,
        question: question.trim(),
        historyPairs: qaHistoryPairs.length
      });

      const response = await fetch(`${baseUrl}/notes/qa/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const finalAnswer = result.answer || '';
      const fetchedReferences = [];
      const fetchedConfidence = 1;

      setCurrentQaHistoryLocal(prev => prev.map(msg =>
        msg.id === aiMessageId ? { ...msg, content: finalAnswer, references: fetchedReferences, confidence: fetchedConfidence, isStreaming: false } : msg
      ));
      currentQaHistoryRef.current = currentQaHistoryRef.current.map(msg =>
        msg.id === aiMessageId ? { ...msg, content: finalAnswer, references: fetchedReferences, confidence: fetchedConfidence, isStreaming: false } : msg
      );

      const qaResult = { answer: finalAnswer, references: fetchedReferences, confidence: fetchedConfidence, timestamp: new Date().toLocaleTimeString() };

      return qaResult;

    } catch (error) {
      console.error('[NoteListAsk] qaSearchCurrent 失败:', error);
      const errorMessage = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('dashboard:qa_service_unavailable', { defaultValue: 'Sorry, the Q&A service is temporarily unavailable. Please try again later.' });
      setCurrentQaHistoryLocal(prev => prev.map(msg =>
        msg.id === aiMessageId ? { ...msg, content: errorMessage, isStreaming: false } : msg
      ));

      return null;
    } finally {
      setCurrentIsQASearchLoading(false);
    }
  }, [selectedNote, t]);

  // Derived qaSearch based on contextMode
  const qaSearch = contextMode === 'all' ? qaSearchAll : qaSearchCurrent;

  // 同步 qaSearch 函数到对应 ref
  useEffect(() => {
    allQaSearchRef.current = qaSearchAll;
  }, [qaSearchAll]);

  useEffect(() => {
    currentQaSearchRef.current = qaSearchCurrent;
  }, [qaSearchCurrent]);
  
  // 当 initialQuestion 变化时，自动触发 QA 搜索
  useEffect(() => {
    if (initialQuestion && initialQuestion.trim()) {
      const trimmedQuestion = initialQuestion.trim();
      // 检查是否与已处理的问题不同
      if (processedInitialQuestionRef.current !== trimmedQuestion) {
        console.log('[NoteListAsk] 检测到初始问题，自动触发 QA 搜索:', trimmedQuestion);
        processedInitialQuestionRef.current = trimmedQuestion;
        // 设置输入框内容
        setQuestionInput(trimmedQuestion);
        // 延迟触发搜索，确保组件已完全渲染且 qaSearch 函数已准备好
        setTimeout(() => {
          if (qaSearchRef.current) {
            console.log('[NoteListAsk] 执行 QA 搜索，问题:', trimmedQuestion);
            qaSearchRef.current(trimmedQuestion, 'qa');
          } else {
            console.warn('[NoteListAsk] qaSearchRef.current 为空，无法执行搜索');
          }
        }, 100);
      } else {
        console.log('[NoteListAsk] 初始问题与已处理的问题相同，跳过触发:', trimmedQuestion);
      }
    } else if (!initialQuestion) {
      // 如果 initialQuestion 被清除，重置处理标志（允许下次相同问题时再次触发）
      // 注意：这里不清除 processedInitialQuestionRef，因为如果用户再次搜索相同关键词，不应该重复触发
      // processedInitialQuestionRef.current = null;
    }
  }, [initialQuestion]);

  // 处理发送问题
  const handleSendQuestion = useCallback(async () => {
    const trimmedQuestion = questionInput.trim();
    if (!trimmedQuestion || isQASearchLoading) {
      return;
    }

    // 清空输入框
    setQuestionInput('');

    // 调用 qaSearch，它会自动获取当前的筛选条件（topic, KB, folder, tag, searchKeyword）
    // 使用 'qa' 模式进行问答搜索
    await qaSearch(trimmedQuestion, 'qa');
  }, [questionInput, isQASearchLoading, qaSearch, setQuestionInput]);

  // 处理输入框按键事件
  const handleInputKeyPress = useCallback((e) => {
    // Shift+Enter 换行，Enter 发送
    if (e.shiftKey) {
      return; // 允许默认的换行行为
    }

    // 普通 Enter 键：阻止默认行为（换行）并触发发送
    e.preventDefault();
    if (questionInput.trim()) {
      handleSendQuestion();
    }
  }, [questionInput, handleSendQuestion]);

  // 处理清空输入框
  const handleClearInput = useCallback(() => {
    setQuestionInput('');
  }, []);

  // 提取问答内容渲染逻辑
  const renderQAContent = () => {
    // 将问答配对，每次显示一组（问题+回答）
    const qaPairs = [];
    for (let i = 0; i < qaHistory.length; i++) {
      if (qaHistory[i].type === 'user') {
        const aiResponse = qaHistory[i + 1] && qaHistory[i + 1].type === 'ai' ? qaHistory[i + 1] : null;
        qaPairs.push({
          question: qaHistory[i],
          answer: aiResponse,
          pairIndex: qaPairs.length + 1
        });
        if (aiResponse) i++; // 跳过已配对的 AI 回答
      }
    }

    if (qaHistory.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
          {qaPairs.map((pair, pairIndex) => (
            <div
              key={pair.question.id}
              data-qa-pair-id={pair.question.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
              {/* 用户问题 - Figma style: right-aligned, blue background */}
              <div
                data-question-id={pair.question.id}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}>
                <div style={{
                  maxWidth: '80%',
                  backgroundColor: '#eff6ff',
                  color: '#1e3a5f',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  borderBottomRightRadius: '2px'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: '1.625',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {pair.question.content}
                  </p>
                </div>
              </div>

              {/* AI 回答 - Figma style: left-aligned, gray background, purple sparkle icon */}
              {pair.answer ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: '12px'
                }}>
                  {/* AI Avatar - purple background with sparkle icon */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <SparklesIcon style={{ color: '#3b82f6', width: '16px', height: '16px' }} />
                  </div>
                  <div style={{
                    maxWidth: '80%',
                    backgroundColor: '#f3f4f6',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    borderBottomLeftRadius: '2px'
                  }}>
                    <div style={{ fontSize: 14, lineHeight: '20px', color: '#111827', fontWeight: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', WebkitFontSmoothing: 'antialiased' }}>
                      {/* 流式加载中且无有效内容时显示 Thinking 动画 */}
                      {pair.answer.isStreaming && (!pair.answer.content || pair.answer.content === '' || pair.answer.content === '0' || !pair.answer.content.trim()) ? (
                        <span style={{ color: '#666', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#3b82f6', display: 'inline-block', animation: 'spin 2s linear infinite' }}>✦</span>
                          <span>{t('dashboard:thinking', { defaultValue: 'Thinking' })}</span>
                          <span className="thinking-dots" style={{ display: 'inline-flex', gap: '2px' }}>
                            <span style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0s', color: '#3b82f6' }}>●</span>
                            <span style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.2s', color: '#3b82f6' }}>●</span>
                            <span style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.4s', color: '#999' }}>●</span>
                          </span>
                          <style>{`
                            @keyframes pulse {
                              0%, 80%, 100% { opacity: 0.4; }
                              40% { opacity: 1; }
                            }
                            @keyframes spin {
                              from { transform: rotate(0deg); }
                              to { transform: rotate(360deg); }
                            }
                          `}</style>
                        </span>
                      ) : (
                        pair.answer.content && pair.answer.content !== '0' && pair.answer.content.trim() ? (
                          <div
                            className="markdown-content"
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdownSafely(cleanAnswerContent(pair.answer.content))
                            }}
                          />
                        ) : (pair.answer.isStreaming ? (
                          <span style={{ color: '#666', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#3b82f6', display: 'inline-block', animation: 'spin 2s linear infinite' }}>✦</span>
                            <span>{t('dashboard:thinking', { defaultValue: 'Thinking' })}</span>
                            <span className="thinking-dots" style={{ display: 'inline-flex', gap: '2px' }}>
                              <span style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0s', color: '#3b82f6' }}>●</span>
                              <span style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.2s', color: '#3b82f6' }}>●</span>
                              <span style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.4s', color: '#999' }}>●</span>
                            </span>
                          </span>
                        ) : null)
                      )}
                      {!pair.answer.isStreaming && pair.answer.confidence > 0 && pair.answer.confidence <= 0.1 && (
                        <span style={{ color: '#faad14', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                          {t('dashboard:content_from_internet', { defaultValue: '(The above content is from the internet)' })}
                        </span>
                      )}
                    </div>
                    {/* 显示引用笔记列表 */}
                    {!pair.answer.isStreaming && pair.answer.references && Array.isArray(pair.answer.references) && pair.answer.references.length > 0 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px', fontWeight: 500, color: '#6b7280' }}>
                          {t('dashboard:references', { defaultValue: 'References' })} ({pair.answer.references.length})
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {pair.answer.references.map((ref, refIndex) => (
                            <div
                              key={refIndex}
                              onClick={() => {
                                if (ref.noteId) {
                                  const noteId = ref.noteId.toString();
                                  sessionStorage.setItem('noteContent_noteId', noteId);
                                  let noteContentWindow = window.open('/note-content', 'noteContent');
                                  if (noteContentWindow) {
                                    try {
                                      if (noteContentWindow.location.href.includes('/note-content')) {
                                        noteContentWindow.postMessage({
                                          type: 'noteIdChanged',
                                          noteId: noteId
                                        }, window.location.origin);
                                      }
                                    } catch (e) {}
                                    noteContentWindow.focus();
                                  }
                                } else if (ref.sourceUrl) {
                                  window.open(ref.sourceUrl, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              style={{
                                padding: '6px 10px',
                                backgroundColor: '#ffffff',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                cursor: (ref.noteId || ref.sourceUrl) ? 'pointer' : 'default',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (ref.noteId || ref.sourceUrl) {
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                  e.currentTarget.style.borderColor = '#3b82f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (ref.noteId || ref.sourceUrl) {
                                  e.currentTarget.style.backgroundColor = '#ffffff';
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                }
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FileTextOutlined style={{ color: '#3b82f6', fontSize: '14px', flexShrink: 0 }} />
                                <Text
                                  strong
                                  style={{
                                    fontSize: '13px',
                                    color: (ref.noteId || ref.sourceUrl) ? '#3b82f6' : '#374151',
                                    cursor: (ref.noteId || ref.sourceUrl) ? 'pointer' : 'default'
                                  }}
                                >
                                  {ref.title || t('dashboard:untitled_note', { defaultValue: 'Untitled Note' })}
                                </Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // 如果还没有回答，显示加载中 - Figma style
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <SparklesIcon style={{ color: '#3b82f6', width: '16px', height: '16px' }} />
                  </div>
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    borderBottomLeftRadius: '2px'
                  }}>
                    <Spin size="small" />
                    <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px', lineHeight: '1.625' }}>
                      {t('dashboard:ai_thinking', { defaultValue: 'AI is thinking...' })}
                    </Text>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } else if (isQASearchLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '12px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <SparklesIcon style={{ color: '#3b82f6', width: '16px', height: '16px' }} />
          </div>
          <div style={{
            backgroundColor: '#f3f4f6',
            padding: '12px 16px',
            borderRadius: '8px',
            borderBottomLeftRadius: '2px'
          }}>
            <Spin size="small" />
            <Text type="secondary" style={{ fontSize: '14px', marginLeft: '8px', lineHeight: '1.625' }}>
              {t('dashboard:ai_thinking', { defaultValue: 'AI is thinking...' })}
            </Text>
          </div>
        </div>
      );
    } else {
      // Empty state - Figma style welcome message
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '12px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <SparklesIcon style={{ color: '#3b82f6', width: '16px', height: '16px' }} />
          </div>
          <div style={{
            maxWidth: '80%',
            backgroundColor: '#f3f4f6',
            padding: '12px 16px',
            borderRadius: '8px',
            borderBottomLeftRadius: '2px'
          }}>
            <p style={{
              margin: 0,
              fontSize: 14,
              lineHeight: '20px',
              color: '#111827',
              fontWeight: 400,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              WebkitFontSmoothing: 'antialiased'
            }}>
              {t('dashboard:ai_welcome_message', { defaultValue: "Hi! I'm your AI assistant. I can help you understand your notes, find information, or answer questions. What would you like to know?" })}
            </p>
          </div>
        </div>
      );
    }
  };

  // Get folder name for context display
  const currentFolder = getSelectedFolder();
  const currentTopic = getSelectedTopic();
  const currentKB = getSelectedKB();
  const folderDisplayName = currentTopic?.name || currentKB?.name || currentFolder || t('dashboard:all_notes', { defaultValue: 'All Notes' });

  // 参考 KnowledgeCard.js 的结构：返回 Fragment，包含 preview-header 和 note-content-preview
  // 当 embedded=true 时，不添加外层容器样式
  const content = (
    <>
      {/* Back to Summary button */}
      {onBackToSummary && (
        <div
          onClick={onBackToSummary}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            borderBottom: '1px solid #e5e7eb',
            cursor: 'pointer',
            flexShrink: 0,
            color: '#6b7280',
            fontSize: '14px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#111827'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280'; }}
        >
          <FileTextOutlined style={{ fontSize: '14px' }} />
          <span>{t('dashboard:back_to_main', { defaultValue: 'Back to main page' })}</span>
        </div>
      )}

      {/* Content 区域 - 类似 KnowledgeCard 的 note-content-preview */}
      <div 
        className="note-content-preview"
        ref={qaContentRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          backgroundColor: '#ffffff',
          minHeight: 0 // 确保可以正确计算 flex
        }}
      >
        <style>
          {`
            /* Match ChatPanel font styles for markdown content */
            .note-content-preview .markdown-content p {
              margin: 0 !important;
              font-size: 14px !important;
              line-height: 20px !important;
              font-weight: 400 !important;
              font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
              -webkit-font-smoothing: antialiased !important;
            }
            .note-content-preview .markdown-content p + p {
              margin-top: 8px !important;
            }
            .note-content-preview .markdown-content p:first-child {
              margin-top: 0 !important;
            }
            .note-content-preview .markdown-content p:last-child {
              margin-bottom: 0 !important;
            }
            .note-content-preview .markdown-content ul,
            .note-content-preview .markdown-content ol {
              margin: 8px 0 !important;
              padding-left: 20px !important;
            }
            .note-content-preview .markdown-content li {
              font-size: 14px !important;
              line-height: 20px !important;
              font-weight: 400 !important;
              font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
              -webkit-font-smoothing: antialiased !important;
            }
          `}
        </style>
        {renderQAContent()}
      </div>

      {/* 底部固定的问答输入框 - Figma style */}
      <div style={{
        flexShrink: 0,
        padding: '16px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#ffffff'
      }}>
        {/* Context mode options */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '8px'
        }}>
          <div
            onClick={() => setContextMode('all')}
            style={{
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '13px',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: contextMode === 'all' ? '#3b82f6' : '#d1d5db',
              backgroundColor: contextMode === 'all' ? '#eff6ff' : '#ffffff',
              color: contextMode === 'all' ? '#3b82f6' : '#6b7280',
              fontWeight: contextMode === 'all' ? 500 : 400,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
          >
            {folderDisplayName}
          </div>
          {selectedNote && (
            <div
              onClick={() => setContextMode('current')}
              style={{
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '13px',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: contextMode === 'current' ? '#3b82f6' : '#d1d5db',
                backgroundColor: contextMode === 'current' ? '#eff6ff' : '#ffffff',
                color: contextMode === 'current' ? '#3b82f6' : '#6b7280',
                fontWeight: contextMode === 'current' ? 500 : 400,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px'
              }}
            >
              {selectedNote.title || t('dashboard:untitled_note', { defaultValue: 'Untitled Note' })}
            </div>
          )}
        </div>
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-end'
        }}>
          <TextArea
            placeholder={
              contextMode === 'current' && selectedNote
                ? t('dashboard:ask_about_note_placeholder', { defaultValue: 'Ask about "{{title}}"...', title: selectedNote.title || 'this note' })
                : t('dashboard:ask_about_notes_placeholder', { defaultValue: 'Ask about your notes...' })
            }
            value={questionInput}
            onChange={(e) => setQuestionInput(e.target.value)}
            onPressEnter={handleInputKeyPress}
            autoSize={{ minRows: 2, maxRows: 4 }}
            disabled={isQASearchLoading}
            style={{
              flex: 1,
              resize: 'none',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              padding: '8px 12px',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          />
          <Button
            type="primary"
            icon={<SendIcon style={{ color: '#ffffff' }} />}
            onClick={handleSendQuestion}
            disabled={!questionInput.trim() || isQASearchLoading}
            loading={isQASearchLoading}
            style={{
              height: '40px',
              width: '40px',
              minWidth: '40px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              backgroundColor: questionInput.trim() && !isQASearchLoading ? '#3b82f6' : '#d1d5db',
              border: 'none'
            }}
          />
        </div>
      </div>
    </>
  );

  // 如果是在 RightContentPane 中嵌入，直接返回内容，不添加外层容器
  if (embedded) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}>
        {content}
      </div>
    );
  }

  // 原始实现：用于左侧面板（30%宽度），添加外层容器样式
  return (
    <div style={{
      width: width,
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #eee',
      borderRadius: '8px',
      backgroundColor: '#fff',
      overflow: 'hidden',
      height: '100%'
    }}>
      {content}
    </div>
  );
};

export default NoteListAsk;
